import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  MEU_EXTRATO_FINANCEIRO,
  MeuExtratoFinanceiroData,
  MeuExtratoFinanceiroVars,
} from '../services/financeiroService';
import type { ExtratoLinha, FiltrosExtrato, PeriodoFinanceiro } from '../types/financeiro';

// Hook dono da consulta do extrato (financeiro-dashboard-atleta Phase 4, Task 7 do plano;
// AC-A2/AC-A3). Regras do corpo:
// 1. Filtros e paginação são variáveis da query — mudá-los muda `variables`, e o Apollo dispara
//    nova consulta. O hook nunca filtra ou fatia `items` — é a AC-8 inteira.
// 2. Lista vazia enviada como `null` (não `[]`): `[]` significaria "nenhum tipo/status aceito"
//    e devolveria vazio; `null`/ausente é "sem filtro".
// 3. Trocar filtro ou período volta para `page: 0` — permanecer numa página que deixou de existir
//    mostraria vazio sem explicação.
// 4. Nenhum useMemo/useCallback sem justificativa (ai/anti-patterns.md §7); nenhuma soma de valor.

export interface UseExtratoFinanceiroResult {
  linhas: ExtratoLinha[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  filtros: FiltrosExtrato;
  setFiltros: (filtros: FiltrosExtrato) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function useExtratoFinanceiro(periodo: PeriodoFinanceiro): UseExtratoFinanceiroResult {
  const [page, setPageState] = useState(0);
  const [size, setSizeState] = useState(20);
  const [filtros, setFiltrosState] = useState<FiltrosExtrato>({ tipos: [], status: [] });

  const query = useQuery<MeuExtratoFinanceiroData, MeuExtratoFinanceiroVars>(MEU_EXTRATO_FINANCEIRO, {
    variables: {
      filtro: periodo,
      tipos: filtros.tipos.length ? filtros.tipos : null,
      status: filtros.status.length ? filtros.status : null,
      page,
      size,
    },
    fetchPolicy: 'no-cache',
  });

  const setFiltros = (novosFiltros: FiltrosExtrato) => {
    setFiltrosState(novosFiltros);
    setPageState(0);
  };

  const setPage = (novaPage: number) => {
    setPageState(novaPage);
  };

  const setSize = (novoSize: number) => {
    setSizeState(novoSize);
    setPageState(0);
  };

  const conexao = query.data?.meuExtratoFinanceiro ?? null;

  return {
    linhas: conexao?.items ?? [],
    page,
    size,
    totalItems: conexao?.totalItems ?? 0,
    totalPages: conexao?.totalPages ?? 0,
    filtros,
    setFiltros,
    setPage,
    setSize,
    loading: query.loading,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
